import { useMemo } from 'react';

import { useInViewport } from '@cast/shared-layout-ui';
import { useClusterCostReportQuery } from '@cast/shared-cost-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { mapClusterCostReportToSpendBreakdown } from '@cast/features-cluster-shared-utils';
import { formatCurrency, getCurrencyPrecision } from '@cast/utils';

import { useClusterDashboardOverviewQueryArgs } from './useClusterDashboardOverviewQueryArgs';

const formatMonthlyCostDisplay = (value: number | null): string => {
  if (value == null) {
    return '—';
  }
  return formatCurrency(value, {
    maximumFractionDigits: getCurrencyPrecision(value),
  });
};

export const useClusterDashboardMonthlyCostData = () => {
  const inView = useInViewport();
  const { cluster } = useCluster();
  const { startTime, endTime } = useClusterDashboardOverviewQueryArgs();

  const { costReport, isLoading, error, refetch } = useClusterCostReportQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    enabled: inView,
    granularity: { step: 1, unit: 'day' },
  });

  const {
    chartData,
    currentSpendMonthly,
    onDemandSpendMonthly,
    spotSpendMonthly,
    fallbackSpendMonthly,
  } = useMemo(
    () => mapClusterCostReportToSpendBreakdown({ items: costReport?.items }),
    [costReport?.items]
  );

  return {
    chartData,
    currentSpend: {
      label: 'Current spend',
      value: formatMonthlyCostDisplay(currentSpendMonthly),
      unit: '/mo',
    },
    onDemandSpend: {
      label: 'On-demand',
      value: formatMonthlyCostDisplay(onDemandSpendMonthly),
      unit: '/mo',
    },
    spotSpend: {
      label: 'Spot',
      value: formatMonthlyCostDisplay(spotSpendMonthly),
      unit: '/mo',
    },
    fallbackSpend: {
      label: 'Fallback',
      value: formatMonthlyCostDisplay(fallbackSpendMonthly),
      unit: '/mo',
    },
    isLoading,
    hasError: !!error,
    refetch,
  };
};
